import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { quizApi, type RegistrationRequest } from "@/lib/quizApi";
import { saveSession } from "@/lib/session";
import { BrandHeader } from "@/components/BrandHeader";

type FormValues = Omit<RegistrationRequest, "channel"> & { channel: RegistrationRequest["channel"] };

export default function QuizRegistration() {
  const { quizId = "" } = useParams();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const quiz = useQuery({
    queryKey: ["publicQuiz", quizId],
    queryFn: () => quizApi.getPublic(quizId),
    enabled: !!quizId,
  });

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    defaultValues: { channel: "email" },
  });

  const submit = useMutation({
    mutationFn: (body: RegistrationRequest) => quizApi.register(quizId, body),
    onSuccess: (r) => {
      // OTP-off quiz: server already issued a session token — skip the verify screen.
      if (r.token && r.expiresAt) {
        saveSession({ quizId, candidateId: r.candidateId, token: r.token, expiresAt: r.expiresAt });
        navigate(quiz.data?.idVerificationRequired
          ? `/quiz/${quizId}/id-upload`
          : `/quiz/${quizId}/instructions`);
        return;
      }
      sessionStorage.setItem("pendingCandidateId", r.candidateId);
      navigate(`/quiz/${quizId}/verify`);
    },
    onError: (e: any) => setServerError(e?.response?.data?.detail ?? "Registration failed."),
  });

  if (quiz.isLoading) return <Centered>Loading quiz…</Centered>;
  if (quiz.isError) return <Centered>This quiz is not available.</Centered>;
  const q = quiz.data!;

  // Gate the form on the quiz window — don't let candidates fill in details for a
  // quiz they can't start yet (or one that has already closed).
  if (!q.isOpen) return <QuizWindowGate quiz={q} />;

  const showChannelSelector = q.otpMode === "Either";
  const otpOff = q.otpMode === "None";

  return (
    <>
      <BrandHeader />
      <main className="mx-auto max-w-2xl p-6">
      <div className="card p-8">
        <h1 className="text-2xl font-bold text-brand">{q.title}</h1>
        <p className="mt-1 text-sm text-brand-ink/60">
          {otpOff
            ? "Please fill in your details to begin."
            : `Please fill in your details to begin. Verification code will be sent to your ${
                q.otpMode === "Email" ? "email"
                : q.otpMode === "Sms" ? "mobile"
                : q.otpMode === "Both" ? "email and mobile"
                : "chosen channel"
              }.`}
        </p>

        <form className="mt-6 space-y-5" onSubmit={handleSubmit((v) => submit.mutate(v))}>
          <Field label="Email Address" required error={errors.email?.message}>
            <input className="field-input" placeholder="Enter your registered email address"
              aria-required="true"
              {...register("email", { required: "Email is required" })} />
          </Field>
          <Field label="First Name" required error={errors.firstName?.message}>
            <input className="field-input" placeholder="Enter First Name"
              aria-required="true"
              {...register("firstName", { required: "First name is required" })} />
          </Field>
          <Field label="Last Name" required error={errors.lastName?.message}>
            <input className="field-input" placeholder="Enter Last Name"
              aria-required="true"
              {...register("lastName", { required: "Last name is required" })} />
          </Field>
          <Field label="Father Name" required error={errors.fatherName?.message}>
            <input className="field-input" placeholder="Enter Father Name"
              aria-required="true"
              {...register("fatherName", { required: "Father name is required" })} />
          </Field>
          <Field label="Mobile" required error={errors.mobile?.message}>
            <input className="field-input" placeholder="Enter your registered mobile number"
              aria-required="true"
              {...register("mobile", { required: "Mobile is required" })} />
          </Field>
          <Field label="Category" required error={errors.category?.message}>
            <select className="field-input bg-transparent"
              aria-required="true"
              {...register("category", { required: "Category is required" })}>
              <option value="">Choose Category</option>
              {q.candidateCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Date of Birth" required error={errors.dateOfBirth?.message}>
            <input type="date" className="field-input"
              aria-required="true"
              {...register("dateOfBirth", { required: "Date of birth is required" })} />
          </Field>

          {showChannelSelector && (
            <fieldset className="mt-2">
              <legend className="field-label">Send verification code via</legend>
              <div className="mt-2 flex gap-6 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input type="radio" value="email" {...register("channel")} defaultChecked />
                  Email
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="radio" value="sms" {...register("channel")} />
                  SMS
                </label>
              </div>
            </fieldset>
          )}

          {serverError && <div className="text-sm text-brand">{serverError}</div>}

          <div className="pt-4">
            <button className="btn-primary" type="submit" disabled={submit.isPending}>
              {submit.isPending ? "Sending…" : "Start Exam →"}
            </button>
          </div>
        </form>
      </div>
      </main>
    </>
  );
}

function Field({ label, error, required, children }:
  { label: string; error?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="field-label">
        {label}
        {required && <span className="ml-1 text-brand" aria-hidden>*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {error && <div className="mt-1 text-xs text-brand">{error}</div>}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="grid min-h-screen place-items-center p-6 text-brand-ink/60">{children}</main>;
}

// Rendered when the quiz isn't currently open. Shows a clear message + a live countdown
// when the exam is scheduled to open later. Auto-refreshes to the form the moment
// startAt is reached so candidates don't have to manually reload.
function QuizWindowGate({ quiz }: { quiz: import("@/lib/quizApi").PublicQuiz }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const start = new Date(quiz.startAt).getTime();
  const end = new Date(quiz.endAt).getTime();
  const notYetOpen = now < start;
  const closed = now > end;
  const secsToOpen = Math.max(0, Math.floor((start - now) / 1000));
  const fmt = (ms: number) => new Date(ms).toLocaleString(undefined, {
    dateStyle: "medium", timeStyle: "short",
  });

  useEffect(() => {
    if (notYetOpen && secsToOpen === 0) window.location.reload();
  }, [notYetOpen, secsToOpen]);

  return (
    <>
      <BrandHeader />
      <main className="mx-auto max-w-2xl p-6">
      <div className="card p-8 text-center">
        <h1 className="text-2xl font-bold text-brand">{quiz.title}</h1>

        {notYetOpen ? (
          <>
            <div className="mt-2 inline-block rounded-pill bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
              Too early
            </div>
            <p className="mt-4 text-xl font-semibold text-brand-ink">
              You are too early. The test will start just in time.
            </p>
            <p className="mt-3 text-sm text-brand-ink/70">
              Opens at <b>{fmt(start)}</b> ({quiz.timezoneId}) · Closes at <b>{fmt(end)}</b>
            </p>
            <Countdown seconds={secsToOpen} />
          </>
        ) : closed ? (
          <>
            <div className="mt-2 inline-block rounded-pill bg-brand-light px-3 py-1 text-xs font-semibold text-brand">
              Too late
            </div>
            <p className="mt-4 text-xl font-semibold text-brand-ink">
              You are too late. The test has already been conducted. Better luck next time.
            </p>
            <p className="mt-3 text-sm text-brand-ink/70">
              Closed at <b>{fmt(end)}</b> ({quiz.timezoneId})
            </p>
          </>
        ) : (
          <p className="mt-4 text-sm text-brand-ink/70">This quiz is not currently available.</p>
        )}
      </div>
      </main>
    </>
  );
}

function Countdown({ seconds }: { seconds: number }) {
  if (seconds === 0) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const parts = [
    d > 0 ? `${d}d` : null,
    (d > 0 || h > 0) ? `${h}h` : null,
    (d > 0 || h > 0 || m > 0) ? `${m}m` : null,
    `${s}s`,
  ].filter(Boolean);
  return (
    <div className="mt-6">
      <div className="text-xs uppercase tracking-wide text-brand-ink/60">Opens in</div>
      <div className="mt-1 text-3xl font-bold text-brand">{parts.join(" ")}</div>
    </div>
  );
}
